// TSL Activity Log Component
// Displays TSL execution history for overnight troubleshooting

import { useState, useEffect } from 'react';
import { X, Download, Trash2, Filter, Activity, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { tslActivityService } from '../services/tslActivityService';
import type { TSLActivityEntry, TSLActivityFilter, TSLActivityStats } from '../types/tslActivity';

interface TSLActivityLogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TSLActivityLog({ isOpen, onClose }: TSLActivityLogProps) {
  const [entries, setEntries] = useState<TSLActivityEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<TSLActivityEntry[]>([]);
  const [stats, setStats] = useState<TSLActivityStats | null>(null);
  const [filter, setFilter] = useState<TSLActivityFilter>({});
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadData();
      
      // Add listener for real-time updates
      const handleUpdate = (newEntries: TSLActivityEntry[]) => {
        setEntries(newEntries);
      };
      
      tslActivityService.addListener(handleUpdate);
      
      return () => {
        tslActivityService.removeListener(handleUpdate);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    // Apply filters when entries or filter changes
    const filtered = tslActivityService.getFilteredEntries(filter);
    setFilteredEntries(filtered);
  }, [entries, filter]);

  const loadData = () => {
    const allEntries = tslActivityService.getEntries();
    const activityStats = tslActivityService.getStats();
    
    setEntries(allEntries);
    setStats(activityStats);
  };

  const handleExportCSV = () => {
    const csv = tslActivityService.exportAsCSV(filter);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `tsl-activity-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    const data = tslActivityService.exportData(filter);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `tsl-activity-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClearLogs = () => {
    if (confirm('Are you sure you want to clear all TSL activity logs? This action cannot be undone.')) {
      tslActivityService.clearEntries();
      loadData();
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatPrice = (price: number | undefined) => {
    if (price === undefined) return '-';
    return price.toFixed(6);
  };

  const getEventIcon = (entry: TSLActivityEntry) => {
    if (!entry.success) {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    
    switch (entry.eventType) {
      case 'update':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'enable':
        return <Activity className="w-4 h-4 text-blue-500" />;
      case 'disable':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getEventColor = (entry: TSLActivityEntry) => {
    if (!entry.success) return 'text-red-600 bg-red-50';
    
    switch (entry.eventType) {
      case 'update':
        return 'text-green-600 bg-green-50';
      case 'enable':
        return 'text-blue-600 bg-blue-50';
      case 'disable':
        return 'text-yellow-600 bg-yellow-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <Activity className="w-6 h-6 text-blue-500" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                TSL Activity Log
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Trailing Stop-Loss execution history for troubleshooting
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Total Entries:</span>
                <span className="ml-2 font-medium">{stats.totalEntries}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Successful:</span>
                <span className="ml-2 font-medium text-green-600">{stats.successfulUpdates}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Failed:</span>
                <span className="ml-2 font-medium text-red-600">{stats.failedUpdates}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Symbols:</span>
                <span className="ml-2 font-medium">{stats.symbolsTracked.length}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Last Activity:</span>
                <span className="ml-2 font-medium">
                  {stats.lastActivity ? new Date(stats.lastActivity).toLocaleTimeString() : 'None'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                  showFilters 
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Showing {filteredEntries.length} of {entries.length} entries
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={handleExportCSV}
                className="flex items-center space-x-2 px-3 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
              <button
                onClick={handleExportJSON}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Export JSON</span>
              </button>
              <button
                onClick={handleClearLogs}
                className="flex items-center space-x-2 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Clear Logs</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Symbol
                </label>
                <select
                  value={filter.symbol || ''}
                  onChange={(e) => setFilter({ ...filter, symbol: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                >
                  <option value="">All Symbols</option>
                  {stats?.symbolsTracked.map(symbol => (
                    <option key={symbol} value={symbol}>{symbol}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Event Type
                </label>
                <select
                  value={filter.eventType || ''}
                  onChange={(e) => setFilter({ ...filter, eventType: e.target.value as any || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                >
                  <option value="">All Events</option>
                  <option value="update">Updates</option>
                  <option value="enable">Enable</option>
                  <option value="disable">Disable</option>
                  <option value="error">Errors</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={filter.success === undefined ? '' : filter.success.toString()}
                  onChange={(e) => setFilter({ 
                    ...filter, 
                    success: e.target.value === '' ? undefined : e.target.value === 'true'
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                >
                  <option value="">All Status</option>
                  <option value="true">Success</option>
                  <option value="false">Failed</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time Range
                </label>
                <select
                  onChange={(e) => {
                    const hours = parseInt(e.target.value);
                    if (hours === 0) {
                      setFilter({ ...filter, dateFrom: undefined, dateTo: undefined });
                    } else {
                      const now = Date.now();
                      setFilter({ 
                        ...filter, 
                        dateFrom: now - (hours * 60 * 60 * 1000),
                        dateTo: now
                      });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                >
                  <option value="0">All Time</option>
                  <option value="1">Last Hour</option>
                  <option value="6">Last 6 Hours</option>
                  <option value="24">Last 24 Hours</option>
                  <option value="48">Last 48 Hours</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Activity List */}
        <div className="flex-1 overflow-auto">
          {filteredEntries.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <div className="text-center">
                <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No TSL activity found</p>
                <p className="text-sm">TSL events will appear here as they occur</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredEntries.map((entry) => (
                <div
                  key={entry.id}
                  className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${getEventColor(entry)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      {getEventIcon(entry)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {entry.symbol}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Position {entry.positionId}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            entry.success 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {entry.eventType}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                          {entry.reason}
                        </p>
                        
                        {entry.eventType === 'update' && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-600 dark:text-gray-400">
                            <div>
                              <span className="font-medium">Previous Stop:</span>
                              <span className="ml-1">{formatPrice(entry.previousStopPrice)}</span>
                            </div>
                            <div>
                              <span className="font-medium">New Stop:</span>
                              <span className="ml-1">{formatPrice(entry.newStopPrice)}</span>
                            </div>
                            <div>
                              <span className="font-medium">Market Price:</span>
                              <span className="ml-1">{formatPrice(entry.currentMarketPrice)}</span>
                            </div>
                            <div>
                              <span className="font-medium">Trailing:</span>
                              <span className="ml-1">
                                {entry.trailingDistance}
                                {entry.trailingType === 'percentage' ? '%' : '$'}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {entry.errorMessage && (
                          <div className="mt-2 p-2 bg-red-50 dark:bg-red-900 rounded text-sm text-red-700 dark:text-red-300">
                            <span className="font-medium">Error:</span> {entry.errorMessage}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                      <div>{formatTimestamp(entry.timestamp)}</div>
                      {entry.processingDuration && (
                        <div className="mt-1">
                          {entry.processingDuration}ms
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
